/**
 * NAV-FOCUS
 * Focus page with back button on top
 */

import { withTranslation, WithTranslation } from "react-i18next"
import { faArrowLeft, IconDefinition } from "@fortawesome/free-solid-svg-icons"
import Space from "@/components/space"
import { useMediaQuery } from "react-responsive"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import SaveIcon from "@/components/save-icon"
import Button, { ButtonClassName } from "@/components/button"
import { Session, SessionDashboardTab } from "@/redux/_session.types"
import { connect } from "react-redux"
import { FadeIn } from "@/utils/animations.utils"
import NavTab from "@/components/nav-tab"
import { NavigateFunction, useNavigate } from "react-router-dom"
import { ReactNode } from "react"
import Tooltip from "@/components/tooltip"

export interface NavFocusAction {
  id: string
  node?: ReactNode //Custom element displayed instead of the button
  icon?: IconDefinition
  disabled?: boolean
  isLarge?: boolean
  isStatic?: boolean
  label?: string
  className?: ButtonClassName
  priority?: number
  count?: number //Badge displayed on the button
  countColor?: "red" | "orange" //Urgency of the badge (red by default)
}

interface StateProps extends WithTranslation {
  _session: Session
}

interface OwnProps {
  children: any
  customActions?: ReactNode
  customNavItems?: ReactNode
  isBackDisabled?: boolean
  isCustomText?: boolean
  isFullWidth?: boolean
  isNotScrollable?: boolean
  isWithMarginTop?: boolean
  isWithoutPadding?: boolean
  onAction?: Function
  onBack?: Function
  status?: string
  tabs?: SessionDashboardTab[]
  text?: string
  actions?: NavFocusAction[]
}

type Props = StateProps & OwnProps

function NavFocus(props: Props) {
  const { t } = props
  const navigate: NavigateFunction = useNavigate()

  const isSmall = useMediaQuery({ query: "(max-width : 1200px)" }) //Show small (media query)
  const isWide = useMediaQuery({ query: "(min-width : 1600px)" }) //Enough room for the message of the save

  //Un onglet peut etre le prefixe d'un autre (ex : /prevention-actions et /prevention-actions/in-place) :
  //seul le chemin le plus long qui matche s'allume
  const activeTabId = props.tabs?.reduce<string | null>((active, tab) => {
    const isMatch = tab.id === window.location.pathname || window.location.pathname.startsWith(`${tab.id}/`)
    return isMatch && (active === null || tab.id.length > active.length) ? tab.id : active
  }, null)
  const actions = props.actions
    ?.map((action, index) => ({ action, index }))
    .sort((a, b) => {
      const getGroup = (action: NavFocusAction) => {
        if (!action.isLarge) return 0
        if (action.className === "primary" || action.className === "secondary") return 2
        return 1
      }

      return (
        getGroup(a.action) - getGroup(b.action) ||
        (a.action.priority ?? 100) - (b.action.priority ?? 100) ||
        a.index - b.index
      )
    })
    .map(({ action }) => action)

  //Clic on back button
  //if route back is defined go to this page
  //Else look for the last page
  function back() {
    if (props.onBack) {
      props.onBack()
    } else {
      navigate(-1) // -> props.history.goBack()
    }
  }

  //Click on action on the top
  function clickAction(actionId) {
    if (props.onAction) {
      props.onAction(actionId)
    }
  }

  //Option if scroll is impossible
  function getPageNavigationClass() {
    let className = "flex1 page-navigation-children flex flex-dcol"

    if (!props.isNotScrollable) {
      className += " page-navigation-scrollable"
    }

    return className
  }

  return (
    <div>
      {/** header */}
      <div className="flex page-navigation-menutop grey-bor">
        {props.isBackDisabled ? (
          <div style={{ width: 20 }}></div>
        ) : (
          <div className="page-navigation-back" onClick={back}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </div>
        )}

        {props.text ? (
          <div className="page-navigation-back-text">
            {!props.isCustomText && !props.isBackDisabled ? t("utils_back_to") + " " : null}
            <span>{props.text}</span>
          </div>
        ) : (
          !props.tabs && (
            <div className="page-navigation-back-text">
              <b>{t("utils_back")}</b>
            </div>
          )
        )}

        {props.tabs?.map((tab: SessionDashboardTab) => (
          <NavTab
            isLarge={tab.isLarge}
            key={tab.id}
            id={tab.id}
            icon={tab.icon}
            isActive={tab.id === activeTabId}
            onClick={() => clickAction(tab.id)}
            label={t(tab.label)}
          />
        ))}

        <div className="width-10" />

        <div className="flex flex-auto flex-dcol">{props.customNavItems}</div>

        <Space />

        {/** wide screen : the message "Changes saved" has its space reserved, the title and the buttons do not move when it appears.
            Narrow screen : only the check icon (no message), the space is for the title and the buttons */}
        {props.status && (
          <div className="flex shrink-0 justify-end" style={{ minWidth: isWide ? 170 : undefined }}>
            <SaveIcon status={props.status} isStatusDisplayed={isWide} />
          </div>
        )}

        {props.customActions && props.customActions}

        {actions && (
          <div className="flex shrink-0">
            {actions.map((action) =>
              action.node ? (
                <div className="flex items-center" key={action.id}>
                  {action.node}
                </div>
              ) : action.isStatic ? (
                <div key={action.id} className="grey-t my-auto px-10 text-sm font-bold">
                  {action.label}
                </div>
              ) : (
                <Tooltip text={action.isLarge ? null : action.label} position="bottom" key={action.id}>
                  <Button
                    className={action.className ? action.className : undefined}
                    disabled={action.disabled}
                    isWithBorder={action.isLarge}
                    onClick={() => clickAction(action.id)}>
                    {action.count ? (
                      <div className="relative mr-2">
                        {action.icon && <FontAwesomeIcon icon={action.icon} />}
                        <span
                          className={
                            "absolute z-10 -right-[1.4rem] -top-[0.1875rem] flex h-4 min-w-4 items-center justify-center rounded-full text-sm font-bold leading-none text-white " +
                            (action.countColor === "orange" ? "bg-idt-orange-main" : "bg-idt-red-main") +
                            (action.count > 9 ? " px-1" : "")
                          }>
                          {action.count}
                        </span>
                      </div>
                    ) : !action.isLarge || isSmall ? (
                      <div>
                        {action.icon &&
                          (action.className === "secondary" ? (
                            <FadeIn>
                              <FontAwesomeIcon icon={action.icon} />
                            </FadeIn>
                          ) : (
                            <FontAwesomeIcon icon={action.icon} />
                          ))}
                      </div>
                    ) : (
                      <span className="nav-focus-action-large whitespace-nowrap">
                        {action.icon && <FontAwesomeIcon icon={action.icon} />}
                        {action.label}
                      </span>
                    )}
                  </Button>
                </Tooltip>
              )
            )}
          </div>
        )}
      </div>

      {props.isWithoutPadding ? (
        <div className="flex page-navigation page-navigation-withheader">{props.children}</div>
      ) : (
        <div className="flex page-navigation page-navigation-withheader">
          <div
            id="page-navigation-scrollable"
            className={getPageNavigationClass()}
            style={{
              height: props.isWithMarginTop ? "calc(100vh - 132px)" : "calc(100vh - 88px)",
              marginTop: props.isWithMarginTop ? "44px" : ""
            }}>
            <div className="flex">
              <Space />
              <div style={{ width: props.isFullWidth ? "100vw" : "1000px" }}>{props.children}</div>
              <Space />
            </div>

            <div className="height-60" />
          </div>
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session
})

export default connect(mapStateToProps)(withTranslation()(NavFocus))
